import { Col, Container, Form, Row } from 'react-bootstrap';
import React, { useState } from 'react';
import Select, { SelectOption } from '@amzn/meridian/select';

import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import { Map as ImmutableMap } from 'immutable';
import SelectAttribute from './SelectAttribute';
import Text from '@amzn/meridian/text';
import TextAttribute from './TextAttribute';
import { nanoid } from 'nanoid';
import plusTokens from '@amzn/meridian-tokens/base/icon/plus';

const CUSTOM_ATTRIBUTE_TYPE = '__Custom__';

/*
 * An attribute form that has the ability to add/remove attributes.
 * Input:
 *   1. attributeDefinitions - [name: list[values]] - map of static attributes with their allowed values.
 *   2. value - form values. Has to be an immutable map. Key is the name of the attribute, and value is an array of possible values.
 *   3. onChange - function with 1 parameter - new map after an update.
 *
 * Example of usage:
 * import { Map as ImmutableMap } from 'immutable';
 * const STATIC_ATTRIBUTES_MAP = new ImmutableMap([['AAA_CLIENT', ['CLIENT_1', 'CLIENT_2']],['region', ['us-west-2', 'eu-west-1']]]);
 * const [formData, setFormData] = useState( new ImmutableMap());
 * // Both STATIC_ATTRIBUTES_MAP and formData can be populated from HttpRequests as well.
 * <DynamicAttributeForm
 *   attributeDefinitions="{STATIC_ATTRIBUTES_MAP}"
 *   value="{formData}"
 *   onChange="{(newFormData) => setFormData(newFormData)}"
 * />
 */
const DynamicAttributeForm = ({
  attributeDefinitions,
  value,
  onChange,
  allowCustomAttributes = true,
  disabled = false,
}) => {
  const constantAttributes = new ImmutableMap(attributeDefinitions || []);
  const [newAttributeType, setNewAttributeType] = useState(CUSTOM_ATTRIBUTE_TYPE);

  const changeAttribute = (id, newName, newValue) => {
    const updatedValues = value.set(id, { name: newName, value: newValue });
    onChange(updatedValues);
  };

  const removeAttribute = id => {
    const updatedValues = value.delete(id);
    onChange(updatedValues);
  };

  const addNewAttribute = () => {
    if (constantAttributes.has(newAttributeType)) {
      if (value.filter(value => value.name === newAttributeType).size > 0) {
        return;
      }
    }

    const newId = nanoid();
    const newName = constantAttributes.has(newAttributeType) ? newAttributeType : '';
    const newValue = constantAttributes.has(newAttributeType) ? constantAttributes.get(newAttributeType)[0] : '';

    const updatedValues = value.set(newId, { name: newName, value: newValue });
    onChange(updatedValues);
  };

  return (
    <Container fluid className="pt-3 mb-2 pb-1 pl-1 small-dashed-border">
      {value.isEmpty() && (
        <Text type="b200" color="primary" className="m-1 ml-2">
          No attributes
        </Text>
      )}
      {value
        .entrySeq()
        .toArray()
        .map(([id, { name, value }]) => {
          if (constantAttributes.has(name)) {
            return (
              <SelectAttribute
                key={id}
                id={id}
                name={name}
                allowedValues={constantAttributes.get(name)}
                onChange={changeAttribute}
                onRemoveAttribute={removeAttribute}
                disabled={disabled}
                initialValue={value}
              />
            );
          } else {
            return (
              <TextAttribute
                key={id}
                id={id}
                initialName={name}
                initialValue={value}
                onChange={changeAttribute}
                onRemoveAttribute={removeAttribute}
                disabled={disabled}
              />
            );
          }
        })}
      <hr />
      <Row>
        <Form.Group as={Col} className="col-6 ml-1">
          <Text type="b200" tag="label" color="primary" htmlFor="new-attribute-type">
            Add a new attribute
          </Text>
          <Select
            id="new-attribute-type"
            width="100%"
            placeholder="Select new attribute type"
            value={newAttributeType}
            onChange={setNewAttributeType}
            disabled={disabled}
          >
            {Array.from(constantAttributes.keys()).map(value => {
              return <SelectOption key={value} value={value} label={value} />;
            })}
            {allowCustomAttributes && (
              <SelectOption key={CUSTOM_ATTRIBUTE_TYPE} value={CUSTOM_ATTRIBUTE_TYPE} label={CUSTOM_ATTRIBUTE_TYPE} />
            )}
          </Select>
        </Form.Group>

        <Col className="col-1 d-flex justify-content-center">
          <Button type="tertiary" className="align-self-end mb-3" onClick={addNewAttribute} disabled={disabled}>
            <Icon tokens={plusTokens}></Icon>
          </Button>
        </Col>
      </Row>
    </Container>
  );
};

export default DynamicAttributeForm;
